import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink, Music2 } from "lucide-react";
import { useCuratedPosts, type CuratedPost } from "@/hooks/useCuratedPosts";
import AddFromTikTokDialog from "./AddFromTikTokDialog";

const empty: Partial<CuratedPost> = {
  title: "",
  slug: "",
  summary: "",
  body_md: "",
  cover_image_url: "",
  category: "Community",
  tags: [],
  source_org_name: "",
  source_url: "",
  source_logo_url: "",
  attribution_note: "",
  claim_contact_email: "",
  status: "draft",
};

const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80);

const CuratedPostsTab = () => {
  const qc = useQueryClient();
  const { data: posts = [], isLoading } = useCuratedPosts({ includeDrafts: true });
  const [editing, setEditing] = useState<Partial<CuratedPost> | null>(null);
  const [tagInput, setTagInput] = useState("");
  const [tiktokOpen, setTiktokOpen] = useState(false);

  const save = useMutation({
    mutationFn: async (p: Partial<CuratedPost>) => {
      if (!p.title?.trim()) throw new Error("Title is required");
      if (!p.source_org_name?.trim()) throw new Error("Source organization name is required");
      if (!p.source_url?.trim()) throw new Error("Source URL is required");
      try {
        new URL(p.source_url);
      } catch {
        throw new Error("Source URL must be a valid URL (https://…)");
      }
      const slug = (p.slug?.trim() || slugify(p.title)) as string;
      const payload = {
        title: p.title.trim(),
        slug,
        summary: p.summary?.trim() || null,
        body_md: p.body_md?.trim() || null,
        cover_image_url: p.cover_image_url?.trim() || null,
        category: p.category?.trim() || null,
        tags: p.tags ?? [],
        source_org_name: p.source_org_name.trim(),
        source_url: p.source_url.trim(),
        source_logo_url: p.source_logo_url?.trim() || null,
        attribution_note: p.attribution_note?.trim() || null,
        claim_contact_email: p.claim_contact_email?.trim() || null,
        status: p.status || "draft",
        published_at:
          p.status === "published" ? p.published_at ?? new Date().toISOString() : null,
      };
      if (p.id) {
        const { error } = await supabase
          .from("curated_posts")
          .update(payload)
          .eq("id", p.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("curated_posts").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["curated_posts"] });
      setEditing(null);
      toast.success("Saved");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("curated_posts").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["curated_posts"] });
      toast.success("Deleted");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (editing) {
    const e = editing;
    const setField = (k: keyof CuratedPost, v: any) => setEditing({ ...e, [k]: v });
    return (
      <div className="space-y-4 max-w-2xl">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold">
            {e.id ? "Edit curated post" : "New curated post"}
          </h2>
          <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
        </div>

        <div>
          <Label>Title *</Label>
          <Input value={e.title ?? ""} onChange={(ev) => setField("title", ev.target.value)} />
        </div>
        <div>
          <Label>Slug (auto if blank)</Label>
          <Input value={e.slug ?? ""} onChange={(ev) => setField("slug", ev.target.value)} placeholder="my-curated-post" />
        </div>
        <div>
          <Label>Summary</Label>
          <Textarea rows={2} value={e.summary ?? ""} onChange={(ev) => setField("summary", ev.target.value)} />
        </div>
        <div>
          <Label>Body (Markdown)</Label>
          <Textarea rows={10} value={e.body_md ?? ""} onChange={(ev) => setField("body_md", ev.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Category</Label>
            <Input value={e.category ?? ""} onChange={(ev) => setField("category", ev.target.value)} />
          </div>
          <div>
            <Label>Cover image URL</Label>
            <Input value={e.cover_image_url ?? ""} onChange={(ev) => setField("cover_image_url", ev.target.value)} />
          </div>
        </div>
        <div>
          <Label>Tags (comma-separated)</Label>
          <Input
            value={tagInput || (e.tags ?? []).join(", ")}
            onChange={(ev) => {
              setTagInput(ev.target.value);
              setField("tags", ev.target.value.split(",").map((t) => t.trim()).filter(Boolean));
            }}
          />
        </div>

        <div className="rounded-lg border border-border p-3 space-y-3 bg-muted/20">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Attribution (required)
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Source organization *</Label>
              <Input value={e.source_org_name ?? ""} onChange={(ev) => setField("source_org_name", ev.target.value)} />
            </div>
            <div>
              <Label>Source URL *</Label>
              <Input value={e.source_url ?? ""} onChange={(ev) => setField("source_url", ev.target.value)} placeholder="https://…" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Source logo URL</Label>
              <Input value={e.source_logo_url ?? ""} onChange={(ev) => setField("source_logo_url", ev.target.value)} />
            </div>
            <div>
              <Label>Claim contact email</Label>
              <Input value={e.claim_contact_email ?? ""} onChange={(ev) => setField("claim_contact_email", ev.target.value)} placeholder="hello@org.com" />
            </div>
          </div>
          <div>
            <Label>Attribution note</Label>
            <Input value={e.attribution_note ?? ""} onChange={(ev) => setField("attribution_note", ev.target.value)} placeholder="Shared with permission / CC-BY / fair use" />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Switch
            checked={e.status === "published"}
            onCheckedChange={(c) => setField("status", c ? "published" : "draft")}
          />
          <Label>Published</Label>
        </div>

        <Button onClick={() => save.mutate(e)} disabled={save.isPending}>
          {save.isPending ? "Saving…" : "Save"}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-xl font-semibold">Curated Posts</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setTiktokOpen(true)}>
            <Music2 className="h-4 w-4 mr-2" /> Add from TikTok
          </Button>
          <Button onClick={() => setEditing({ ...empty })}>
            <Plus className="h-4 w-4 mr-2" /> New Curated Post
          </Button>
        </div>
      </div>

      <AddFromTikTokDialog
        open={tiktokOpen}
        onOpenChange={setTiktokOpen}
        onStaged={async (id) => {
          setTiktokOpen(false);
          await qc.invalidateQueries({ queryKey: ["curated_posts"] });
          const { data } = await supabase
            .from("curated_posts")
            .select("*")
            .eq("id", id)
            .maybeSingle();
          if (data) setEditing(data as unknown as CuratedPost);
        }}
      />

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : posts.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground">
          <p className="text-lg mb-1">No curated posts yet</p>
          <p className="text-sm">Spotlight an external organization's work — always with a link back.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <div
              key={p.id}
              className="bg-card border border-border rounded-xl p-5 flex items-center gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    p.status === "published" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                  }`}>
                    {p.status}
                  </span>
                  {p.category && <span className="text-xs text-muted-foreground">{p.category}</span>}
                </div>
                <h3 className="font-display font-semibold truncate">{p.title}</h3>
                <a
                  href={p.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary mt-0.5"
                >
                  <ExternalLink className="h-3 w-3" /> {p.source_org_name}
                </a>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    // Fetch admin-only claim_contact_email via secure RPC
                    const { data: email } = await (supabase as any).rpc(
                      "get_curated_post_claim_email",
                      { _id: p.id },
                    );
                    setEditing({ ...p, claim_contact_email: (email as string) ?? "" });
                  }}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => {
                    if (confirm(`Delete "${p.title}"?`)) del.mutate(p.id);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CuratedPostsTab;